import autoTable from 'jspdf-autotable';

import { formatDate, formatMoney } from '../../../../../../lib/functions/utils';
import { BasePDFGenerator, PDFGeneratorConfig } from '../reportUtils/base';
import { getShortString } from '../reportUtils/documentUtils';
import { ChatMessage } from '../SimulationExportDialog/components/ChatMessagesDialog';

export interface SimulationReportConfig extends PDFGeneratorConfig {
  selectedRequest: any;
  payouts?: any;
}

export class SimulationReportGenerator extends BasePDFGenerator {
  private selectedRequest: any;
  private payouts?: any;

  constructor(config: SimulationReportConfig) {
    super({ ...config, orientation: 'landscape' });
    this.selectedRequest = config.selectedRequest;
    this.payouts = config.payouts;
  }

  protected addSummarySection(): void {
    const selectedRequest = this.selectedRequest;
    const costRate = selectedRequest.documents.reduce(
      (acc: number, doc: any) => acc + doc.costRate,
      0,
    );
    this.doc.setFontSize(10);
    const col1X = 10;
    const col2X = 115;
    const col3X = 210;
    const defaultX = 35;
    const initialY = 30;

    // Columna 1
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Razón Social', col1X, initialY);
    this.doc.setFont('helvetica', 'normal');
    const shortName = getShortString(selectedRequest.businessName, 25);
    this.doc.text(`: ${shortName}`, col1X + defaultX, initialY);

    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Rut Cliente', col1X, initialY + 5);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(
      `: ${selectedRequest.businessIdentifier}`,
      col1X + defaultX,
      initialY + 5,
    );

    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Ejecutivo', col1X, initialY + 10);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(
      `: ${selectedRequest.executiveName}`,
      col1X + defaultX,
      initialY + 10,
    );

    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Nro. Solicitud', col1X, initialY + 15);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(
      `: ${String(selectedRequest.id ? selectedRequest.id : 'N/A')}`,
      col1X + defaultX,
      initialY + 15,
    );

    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Tipo Documento', col1X, initialY + 20);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(
      `: ${selectedRequest.subProductName}`,
      col1X + defaultX,
      initialY + 20,
    );

    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Moneda', col1X, initialY + 25);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(': CLP', col1X + defaultX, initialY + 25);

    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Crea Operación', col1X, initialY + 30);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(
      `: ${selectedRequest.executiveName}`,
      col1X + defaultX,
      initialY + 30,
    );

    // Columna 2
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Fecha Negocio', col2X, initialY);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(
      `: ${formatDate(selectedRequest.operationDate)}`,
      col2X + defaultX,
      initialY,
    );

    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Fecha Cierre', col2X, initialY + 5);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(
      `: ${selectedRequest.financingAt ? formatDate(selectedRequest.financingAt) : 'No ejecutado'}`,
      col2X + defaultX,
      initialY + 5,
    );

    let addY = 10;
    if (selectedRequest.showAdvance) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Anticipo', col2X, initialY + addY);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(
        `: ${String(selectedRequest.advance)}%`,
        col2X + defaultX,
        initialY + addY,
      );
      addY += 5;
    }
    if (
      selectedRequest.showArrearRate &&
      selectedRequest.arrearRate &&
      !selectedRequest.isSimulation
    ) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Tasa Mora', col2X, initialY + addY);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(
        `: ${String(selectedRequest.arrearRate)}%`,
        col2X + defaultX,
        initialY + addY,
      );
      addY += 5;
    }
    if (selectedRequest.showRate) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Diferencia de precio', col2X, initialY + addY);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(
        `: ${String(selectedRequest.rate)}%`,
        col2X + defaultX,
        initialY + addY,
      );
      addY += 5;
    }
    if (selectedRequest.showFundRate && selectedRequest.fundRate) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Tasa del fondo', col2X, initialY + addY);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(
        `: ${String(selectedRequest.fundRate)}%`,
        col2X + defaultX,
        initialY + addY,
      );
      addY += 5;
    }
    if (selectedRequest.showSpread && selectedRequest.spread !== undefined) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Spread', col2X, initialY + addY);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(
        `: ${String(selectedRequest.spread)}%`,
        col2X + defaultX,
        initialY + addY,
      );
      addY += 5;
    }

    // Columna 3
    const alignRight = (text: string, x: number, y: number) => {
      const textWidth = this.doc.getTextWidth(text);
      this.doc.text(':', x + 35 - 25, y);
      this.doc.text(text, x + 35 - textWidth, y);
    };
    addY = 0;
    if (selectedRequest.showAmount) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Monto Total', col3X, initialY + addY);
      this.doc.setFont('helvetica', 'normal');
      alignRight(
        `${formatMoney(selectedRequest.amount)}`,
        col3X + defaultX,
        initialY + addY,
      );
      addY += 5;
    }
    if (selectedRequest.showAmountAdvance) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Monto Anticipado', col3X, initialY + addY);
      this.doc.setFont('helvetica', 'normal');
      alignRight(
        `${formatMoney(selectedRequest.amount * (selectedRequest.advance / 100))}`,
        col3X + defaultX,
        initialY + addY,
      );
      addY += 5;
    }
    if (selectedRequest.showAmountRetained) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Monto Retenido', col3X, initialY + addY);
      this.doc.setFont('helvetica', 'normal');
      alignRight(
        `${formatMoney(((100 - selectedRequest.advance) * selectedRequest.amount) / 100)}`,
        col3X + defaultX,
        initialY + addY,
      );
      addY += 5;
    }
    if (
      selectedRequest.showGuaranteeAmount &&
      selectedRequest.guaranteeAmount
    ) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Monto en garantía', col3X, initialY + addY);
      this.doc.setFont('helvetica', 'normal');
      const guaranteeText = `${formatMoney(selectedRequest.guaranteeAmount)}${selectedRequest.guaranteeObservation ? '*' : ''}`;
      alignRight(guaranteeText, col3X + defaultX, initialY + addY);
      addY += 5;
    }
    if (selectedRequest.showAmountDifference) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Dif precio', col3X, initialY + addY);
      this.doc.setFont('helvetica', 'normal');
      alignRight(`${formatMoney(costRate)}`, col3X + defaultX, initialY + addY);
      addY += 5;
    }
    if (selectedRequest.showAmountComissionIva) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Comisión + IVA', col3X, initialY + addY);
      this.doc.setFont('helvetica', 'normal');
      alignRight(
        `${formatMoney((selectedRequest.operationCostFixed + selectedRequest.operationCostVariable) * 1.19)}`,
        col3X + defaultX,
        initialY + addY,
      );
      addY += 5;
    }
    if (
      selectedRequest.showAmountTotalApplied &&
      selectedRequest.amountToApply
    ) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Total Aplicado', col3X, initialY + addY);
      this.doc.setFont('helvetica', 'normal');
      alignRight(
        `${formatMoney(selectedRequest.amountToApply)}`,
        col3X + defaultX,
        initialY + addY,
      );
      addY += 5;
    }
    const expensesText =
      selectedRequest.expensesCostIva > 0 ? 'Gastos + IVA' : 'Gastos';
    const expensesValue =
      selectedRequest.expenses + selectedRequest.expensesCostIva;
    if (selectedRequest.showExpenses && expensesValue > 0) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text(expensesText, col3X, initialY + addY);
      this.doc.setFont('helvetica', 'normal');
      alignRight(
        `${formatMoney(expensesValue)}`,
        col3X + defaultX,
        initialY + addY,
      );
      addY += 5;
    }
    if (selectedRequest.showDraftBalance) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Saldo Giro', col3X, initialY + addY);
      this.doc.setFont('helvetica', 'normal');
      alignRight(
        `${formatMoney(selectedRequest.amountToDepositTotal)}`,
        col3X + defaultX,
        initialY + addY,
      );
      addY += 5;
    }
  }

  protected addContentSection(): void {
    this.addDocumentsTable();
    if (
      this.selectedRequest.documentsToApply &&
      this.selectedRequest.documentsToApply.length > 0
    ) {
      this.addApplicationsTable();
    }
  }

  private addDocumentsTable(): void {
    const selectedRequest = this.selectedRequest;
    const isSimulation = selectedRequest.isSimulation || false;

    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Documentos', 10, 75);
    this.doc.setFont('helvetica', 'normal');
    autoTable(this.doc, {
      startY: 80,
      head: [
        [
          'Nro. Doc',
          'Rut Deudor',
          ...(isSimulation ? [] : ['Razón social']),
          'Tipo',
          ...(selectedRequest.showAmount ? ['Monto'] : []),
          ...(selectedRequest.showAmountAdvance ? ['Monto Anticipado'] : []),
          ...(selectedRequest.showAmountDifference
            ? ['Diferencia precio']
            : []),
          ...(selectedRequest.showDueDate === false
            ? []
            : ['Fecha vencimiento']),
          ...(selectedRequest.showDays === false ? [] : ['Días']),
          ...(isSimulation ? [] : ['Reclamo']),
          ...(isSimulation ? [] : ['Nota Crédito']),
        ],
      ],
      body: selectedRequest.documents
        .filter((doc: any) => doc.active !== false)
        .map((doc: any) => [
          String(doc.folio),
          doc.stakeholderIdentifier,
          ...(isSimulation ? [] : [doc.stakeholderName]),
          selectedRequest.subProductName,
          ...(selectedRequest.showAmount ? [formatMoney(doc.amount)] : []),
          ...(selectedRequest.showAmountAdvance
            ? [formatMoney(doc.amountFinanced)]
            : []),
          ...(selectedRequest.showAmountDifference
            ? [formatMoney(doc.costRate)]
            : []),
          ...(selectedRequest.showDueDate === false
            ? []
            : [formatDate(doc.paymentDate)]),
          ...(selectedRequest.showDays === false
            ? []
            : [String(doc.daysToCount)]),
          ...(isSimulation ? [] : [doc.reclamo]),
          ...(isSimulation ? [] : [doc.notaCredito]),
        ]),
      theme: 'grid',
      headStyles: {
        fillColor: '#efefef',
        textColor: '#1b1b1b',
        fontStyle: 'bold',
        halign: 'left',
        valign: 'middle',
        lineColor: [44, 62, 80],
        fontSize: 9,
      },
      bodyStyles: {
        textColor: [50, 50, 50],
        fontSize: 8,
      },
      styles: {
        font: 'helvetica',
        lineColor: [44, 62, 80],
        lineWidth: 0.1,
      },
    });
    this.doc.setFontSize(10);
  }

  private addApplicationsTable(): void {
    const selectedRequest = this.selectedRequest;
    this.doc.setFontSize(10);
    const finalY = (this.doc as any).lastAutoTable.finalY || 110;
    this.doc.text('Aplicaciones', 10, finalY + 10);
    autoTable(this.doc, {
      startY: finalY + 15,
      head: [
        [
          'Descripción',
          'N° Operación',
          'N° Documento',
          'Monto Factura',
          'Monto Aplicado',
        ],
      ],
      body: selectedRequest.documentsToApply.map((application: any) => [
        application.type,
        application.requestId,
        application.folio,
        formatMoney(application.amount),
        formatMoney(application.amountToConciliate),
      ]),
      theme: 'grid',
      headStyles: {
        fillColor: '#efefef',
        textColor: '#1b1b1b',
        fontStyle: 'bold',
        halign: 'left',
        valign: 'middle',
        lineColor: [44, 62, 80],
        fontSize: 9,
      },
      bodyStyles: {
        textColor: [50, 50, 50],
        fontSize: 8,
      },
      styles: {
        font: 'helvetica',
        lineColor: [44, 62, 80],
        lineWidth: 0.1,
      },
    });
  }

  public async generate() {
    await this.addTitleSection(this.selectedRequest);
    this.addSummarySection();
    this.addContentSection();
    if (
      this.selectedRequest?.showGuaranteeAmount &&
      this.selectedRequest.guaranteeAmount &&
      this.selectedRequest.guaranteeObservation
    ) {
      this.addGuaranteeObservationFooter(
        this.selectedRequest.guaranteeObservation,
      );
    }
    if (this.payouts) {
      this.doc.addPage();
      await this.addPayoutsSection(this.payouts);
    }
    if (
      this.selectedRequest.showExpensesDetails &&
      this.selectedRequest.expenses > 0
    ) {
      this.doc.addPage();
      await this.addExpensesSection();
    }
    if (
      this.selectedRequest.showChatAsObservations &&
      this.selectedRequest.selectedChatMessages &&
      this.selectedRequest.selectedChatMessages.length > 0
    ) {
      this.doc.addPage();
      await this.addObservationsSection(
        this.selectedRequest.selectedChatMessages,
      );
    }
    this.saveDocument();
  }

  public async addPayoutsSection(payoutGroup: any): Promise<void> {
    if (!payoutGroup || !Array.isArray(payoutGroup.payments)) return;
    await this.addTitleSectionCustom('Pagos Realizados');
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    const payments = payoutGroup.payments;
    autoTable(this.doc, {
      startY: 30,
      head: [
        [
          'Banco Origen',
          'N° Cuenta Origen',
          'Banco Destino',
          'N° Cuenta Destino',
          'RUT Destino',
          'Tipo Cuenta Destino',
          'Titular Cuenta Destino',
          'Giro',
          'Monto',
        ],
      ],
      body:
        payments.length > 0
          ? payments.map((p: any) => [
              p.bankAccountOriginName,
              p.bankAccountOriginNumber,
              p.bankAccountDestinationName,
              p.bankAccountDestinationNumber,
              p.bankAccountDestinationHolderIdentifier,
              p.bankAccountDestinationAccountType,
              p.bankAccountDestinationHolderName,
              p.executedAt ? formatDate(p.executedAt) : '',
              formatMoney(p.amount),
            ])
          : [
              [
                {
                  content: 'No hay pagos realizados',
                  colSpan: 9,
                  styles: { halign: 'center', fontStyle: 'italic' },
                },
              ],
            ],
      theme: 'grid',
      headStyles: {
        fillColor: '#efefef',
        textColor: '#1b1b1b',
        fontStyle: 'bold',
        halign: 'left',
        valign: 'middle',
        lineColor: [44, 62, 80],
        fontSize: 9,
      },
      bodyStyles: {
        textColor: [50, 50, 50],
        fontSize: 8,
      },
      styles: {
        font: 'helvetica',
        lineColor: [44, 62, 80],
        lineWidth: 0.1,
      },
    });
  }

  private async addTitleSectionCustom(title: string): Promise<void> {
    const { userName, imageBase64, orientation } = this.config;

    const imgX = 10,
      imgY = 5,
      imgHeight = 15;
    const { width: naturalWidth, height: naturalHeight } =
      await this.getImageDimensionsFromBase64(imageBase64);
    const aspectRatio = naturalWidth / naturalHeight;
    const imgWidth = imgHeight * aspectRatio;
    this.doc.addImage(
      imageBase64 as any,
      'PNG',
      imgX,
      imgY,
      imgWidth,
      imgHeight,
    );
    const pageWidth = orientation === 'landscape' ? 297 : 210;
    this.drawText(title, pageWidth / 2, 15, {
      size: 16,
      align: 'center',
      bold: true,
    });
    const rightX = orientation === 'landscape' ? 230 : 150;
    this.drawText(`Fecha: ${new Date().toLocaleString()}`, rightX, 10, {
      size: 10,
    });
    const userToShow = this.selectedRequest
      ? this.selectedRequest.showExecutiveNameAsUser
        ? this.selectedRequest.executiveName
        : userName
      : userName;
    this.drawText(`Usuario: ${userToShow}`, rightX, 15, { size: 10 });
  }

  private addGuaranteeObservationFooter(observation: string) {
    const pageHeight = this.doc.internal.pageSize.getHeight();
    this.doc.setFontSize(8);
    this.doc.setFont('helvetica', 'italic');
    // 10 puntos arriba del borde inferior
    this.doc.text(
      `*Observación monto en garantía: ${observation}`,
      15,
      pageHeight - 10,
    );
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(10);
  }

  private async addExpensesSection(): Promise<void> {
    const selectedRequest = this.selectedRequest;
    await this.addTitleSectionCustom('Desglose Gastos');
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    autoTable(this.doc, {
      startY: 30,
      head: [['Gasto', 'Monto', 'IVA', 'Total']],
      body: selectedRequest.expensesDetails
        .filter(
          (expense: { name: string; amount: number; hasIva: boolean }) =>
            expense.amount > 0,
        )
        .map((expense: { name: string; amount: number; hasIva: boolean }) => [
          expense.name,
          formatMoney(expense.amount),
          formatMoney(expense.hasIva ? expense.amount * 0.19 : 0),
          formatMoney(
            expense.amount + (expense.hasIva ? expense.amount * 0.19 : 0),
          ),
        ]),
      theme: 'grid',
      headStyles: {
        fillColor: '#efefef',
        textColor: '#1b1b1b',
        fontStyle: 'bold',
        halign: 'left',
        valign: 'middle',
        lineColor: [44, 62, 80],
        fontSize: 9,
      },
      bodyStyles: {
        textColor: [50, 50, 50],
        fontSize: 8,
      },
      styles: {
        font: 'helvetica',
        lineColor: [44, 62, 80],
        lineWidth: 0.1,
      },
    });
  }

  private addTitleSectionContinuation(title: string): void {
    const { userName, imageBase64, orientation } = this.config;

    // Agregar logo (sin calcular dimensiones para evitar async)
    const imgX = 10;
    const imgY = 5;
    const imgHeight = 15;
    const imgWidth = imgHeight * 3; // Ratio aproximado
    this.doc.addImage(
      imageBase64 as any,
      'PNG',
      imgX,
      imgY,
      imgWidth,
      imgHeight,
    );

    const pageWidth = orientation === 'landscape' ? 297 : 210;
    this.drawText(title, pageWidth / 2, 15, {
      size: 16,
      align: 'center',
      bold: true,
    });
    const rightX = orientation === 'landscape' ? 230 : 150;
    this.drawText(`Fecha: ${new Date().toLocaleString()}`, rightX, 10, {
      size: 10,
    });
    const userToShow = this.selectedRequest
      ? this.selectedRequest.showExecutiveNameAsUser
        ? this.selectedRequest.executiveName
        : userName
      : userName;
    this.drawText(`Usuario: ${userToShow}`, rightX, 15, { size: 10 });
  }

  private async addObservationsSection(messages: ChatMessage[]): Promise<void> {
    await this.addTitleSectionCustom('Observaciones');

    const pageWidth = this.doc.internal.pageSize.getWidth();
    const pageHeight = this.doc.internal.pageSize.getHeight();
    const marginX = 10;
    const marginY = 30;
    const bottomMargin = 20;
    const boxWidth = pageWidth - 2 * marginX;
    const maxBoxHeight = pageHeight - marginY - bottomMargin;
    const padding = 5;
    const textWidth = boxWidth - 2 * padding;
    const lineHeight = 6;

    let currentY = marginY + padding + 5;
    let boxStartY = marginY;

    // Función helper para dibujar la caja
    const drawBox = (startY: number, height: number) => {
      this.doc.setDrawColor(0, 0, 0);
      this.doc.setLineWidth(0.5);
      this.doc.rect(marginX, startY, boxWidth, height);
    };

    // Dibujar la primera caja
    drawBox(boxStartY, maxBoxHeight);

    this.doc.setFontSize(9);
    this.doc.setFont('helvetica', 'normal');

    for (let i = 0; i < messages.length; i++) {
      const message = messages[i];
      const timestamp = formatDate(message.timestamp);
      const userInfo = `${message.userName}${
        message.userRole ? ` (${message.userRole})` : ''
      }`;
      const header = `[${timestamp}] ${userInfo}:`;

      // Calcular altura necesaria para este mensaje
      this.doc.setFont('helvetica', 'bold');
      const headerLines = this.doc.splitTextToSize(header, textWidth);
      this.doc.setFont('helvetica', 'normal');
      const textLines = this.doc.splitTextToSize(message.text, textWidth);
      const messageHeight =
        headerLines.length * lineHeight + textLines.length * lineHeight + 2;

      // Verificar si necesitamos una nueva página
      if (currentY + messageHeight > boxStartY + maxBoxHeight - padding) {
        // Cerrar la caja actual y agregar nueva página
        this.doc.addPage();
        this.addTitleSectionContinuation('Observaciones (continuación)');
        boxStartY = marginY;
        currentY = marginY + padding + 5;
        drawBox(boxStartY, maxBoxHeight);
      }

      // Imprimir el header
      this.doc.setFont('helvetica', 'bold');
      this.doc.text(headerLines, marginX + padding, currentY);
      currentY += headerLines.length * lineHeight;

      // Imprimir el texto del mensaje
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(textLines, marginX + padding, currentY);
      currentY += textLines.length * lineHeight + 2;
    }
  }
}
